import { get, patch, post } from './api.client';

/**
 * Operator console API.
 *
 * Uses the shared client, so the superadmin token stored by `setJwtToken` is
 * attached the same way a tenant token is -- the scope claim inside it, not a
 * different transport, is what grants access.
 */

export type TenantAdminStatus = 'active' | 'invited' | 'invite_expired';

export interface TenantSummary {
  _id: string;
  name: string;
  createdAt: string;
  adminEmail: string | null;
  adminStatus: TenantAdminStatus | null;
  subscriberCount: number;
  lastActivityAt: string | null;
}

export interface TenantEnvironment {
  _id: string;
  name: string;
  identifier: string;
  apiKey: string | null;
}

export interface TenantAdmin {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  status: TenantAdminStatus;
}

export interface TenantDetail {
  organization: {
    _id: string;
    name: string;
    createdAt: string;
    apiServiceLevel?: string;
    memberCount: number;
  };
  environments: {
    development: TenantEnvironment | null;
    production: TenantEnvironment | null;
  };
  admin: TenantAdmin | null;
  counts: {
    subscribers: number;
    workflows: number;
    messagesLast30Days: number;
    lastActivityAt: string | null;
  };
}

export interface Overview {
  totals: { tenants: number; activeTenants: number; subscribers: number } | null;
  messagesByDay: Array<{ date: string; channel: string; count: number }> | null;
  deliveryOutcomes: { delivered: number; bounced: number; suppressed: number } | null;
  newestTenants: Array<{ _id: string; name: string; createdAt: string }> | null;
  recentAuditEvents: Array<{ action: string; createdAt: string; targetId?: string; success: boolean }> | null;
}

export interface AuditEvent {
  _id: string;
  action: string;
  actorId?: string;
  actorIp?: string;
  targetType?: string;
  targetId?: string;
  success: boolean;
  failureReason?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

type Paged<T, K extends string> = { total: number; page: number; limit: number } & Record<K, T[]>;

function unwrap<T>(response: unknown): T {
  // The API wraps successful payloads in { data: ... }.
  const body = response as { data?: T };

  return (body && body.data !== undefined ? body.data : response) as T;
}

export async function fetchOverview(): Promise<Overview> {
  return unwrap<Overview>(await get<unknown>('/superadmin/overview'));
}

export async function fetchTenants(params: { search?: string; page?: number; limit?: number } = {}) {
  const query = new URLSearchParams();

  if (params.search) query.set('search', params.search);
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.limit !== undefined) query.set('limit', String(params.limit));

  const suffix = query.toString() ? `?${query.toString()}` : '';

  return unwrap<Paged<TenantSummary, 'tenants'>>(await get<unknown>(`/superadmin/tenants${suffix}`));
}

export async function fetchTenant(organizationId: string): Promise<TenantDetail> {
  return unwrap<TenantDetail>(await get<unknown>(`/superadmin/tenants/${organizationId}`));
}

export async function createTenant(body: {
  organizationName: string;
  adminFirstName: string;
  adminLastName?: string;
  adminEmail: string;
}) {
  return unwrap<{ organizationId: string; adminUserId: string; invitationSent: boolean; invitationLink?: string }>(
    await post<unknown>('/superadmin/tenants', { body })
  );
}

export async function changeTenantAdminEmail(organizationId: string, newEmail: string) {
  return unwrap<{ success: true; email: string }>(
    await patch<unknown>(`/superadmin/tenants/${organizationId}/admin/email`, { body: { newEmail } })
  );
}

export async function sendTenantAdminPasswordReset(organizationId: string) {
  return unwrap<{ success: true }>(
    await post<unknown>(`/superadmin/tenants/${organizationId}/admin/password-reset`, { body: {} })
  );
}

export async function resendTenantInvitation(organizationId: string) {
  return unwrap<{ invitationSent: boolean; invitationLink?: string }>(
    await post<unknown>(`/superadmin/tenants/${organizationId}/admin/resend-invitation`, { body: {} })
  );
}

export async function fetchAuditLogs(
  params: { action?: string; organizationId?: string; page?: number; limit?: number } = {}
) {
  const query = new URLSearchParams();

  if (params.action) query.set('action', params.action);
  if (params.organizationId) query.set('organizationId', params.organizationId);
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.limit !== undefined) query.set('limit', String(params.limit));

  const suffix = query.toString() ? `?${query.toString()}` : '';

  return unwrap<Paged<AuditEvent, 'events'>>(await get<unknown>(`/superadmin/audit-logs${suffix}`));
}
